import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewInventory } from 'features/Inventories/components/NewInventory'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWriteInventory } from 'features/Users/utils'
import { getDifference } from 'utils/object'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'inventories.edit.helmet',
    defaultMessage: 'Edit Inventory | Nexbus Admin'
  }
})

const CHANGE_INVENTORY = gql`
  mutation ChangeInventory($input: ChangeInventoryInput!) {
    changeInventory(input: $input)
  }
`

const GET_INVENTORY = gql`
  query GetInventory($inventoryId: ID!) {
    inventory(input: { id: $inventoryId }) {
      id
      company {
        id
        name
      }
      name
      type
      address {
        line1
        line2
        city {
          id
          name
        }
        zipCode
      }
      users {
        id
        firstName
        lastName
        username
      }
    }
  }
`

const EditInventoryView = (): TSFixMe => {
  const navigate = useNavigate()
  const { inventoryId } = useParams()
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const [changeInventory, { loading: mutationLoading, error: mutationError }] = useMutation(CHANGE_INVENTORY, {
    onCompleted() {
      navigate('/inventories')
    }
  })

  const handleSubmit = (inventory: TSFixMe): TSFixMe => {
    const previousAddress = !isEmpty(queryData.inventory.address)
      ? {
          line1: queryData.inventory.address.line1,
          line2: queryData.inventory.address.line2,
          cityID: !isEmpty(queryData.inventory.address.city) ? queryData.inventory.address.city.id : null,
          zipCode: queryData.inventory.address.zipCode
        }
      : null
    const previousInventory = {
      name: queryData.inventory.name,
      address: previousAddress,
      userIDs: queryData.inventory.users.map(({ id }: TSFixMe): TSFixMe => id)
    }

    const newInventory = {
      name: inventory.name,
      address: {
        line1: inventory.address.line1,
        line2: inventory.address.line2,
        cityID: inventory.address.city.id || null,
        zipCode: inventory.address.zipCode
      },
      userIDs: inventory.users.map(({ id }: TSFixMe): TSFixMe => id)
    }

    const payload = getDifference(newInventory, previousInventory)

    if (isEmpty(payload)) {
      navigate('/inventories')
    } else {
      changeInventory({
        variables: {
          input: {
            id: inventoryId,
            inventory: {
              ...payload
            }
          }
        }
      })
    }
  }

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_INVENTORY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      inventoryId
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (!canWriteInventory(login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.INVENTORY} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventories.edit.error.description"
              defaultMessage="Hubo un error al cargar tu ubicación"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="spanish.inventories.edit.error.action" defaultMessage="Recarga tu ubicación" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewInventory
        onSubmit={handleSubmit}
        loading={mutationLoading}
        error={mutationError}
        inventory={queryData.inventory || {}}
        isEdit
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="spanish.inventories.edit.title" defaultMessage="Editar ubicación" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayout.Content>
        <Card.Container>{content}</Card.Container>
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(EditInventoryView)
